import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { PersonaAction } from "@/lib/persona";
import {
  Compass, ArrowLeft, ArrowRight, Sparkles, Loader2, Check, Plus, Trash2,
  Building2, MapPin, Layers, Coins, FileText, Target, Network, MessageCircle, X,
  Lock, Pencil, UserPlus,
} from "lucide-react";
import CopyButton from "@/components/CopyButton";
import { SplcButton } from "@/components/AnnuaireSPLC";
import ContextButton from "@/components/ContextButton";
import FreshnessBadge from "@/components/FreshnessBadge";
import { notify, fmtError } from "@/lib/notify";
import { GEO_SCALE_OPTIONS, geoLabel, normalizeGeoLevel } from "@/lib/geoScale";
import SectorCombobox from "@/components/SectorCombobox";
import ThreeQPageHeader from "@/components/ThreeQPageHeader";

const SECTORS = ["Banque", "Assurance", "Industrie", "Santé", "Immobilier", "Tech", "Public", "PME", "Autre"];
// Échelle géographique canonique Noam (cf. /app/frontend/src/lib/geoScale.js)
const GEO_OPTIONS = GEO_SCALE_OPTIONS.map(({ value, label }) => ({ v: value, label }));
const STAGES = ["Création", "Croissance", "Maturité", "Transformation"];
const SIZES = ["TPE", "PME", "ETI", "Grand groupe", "Institution"];
const ASSET_CATEGORIES = [
  "Immobilier résidentiel", "Immobilier commercial", "Actions cotées",
  "Équipements industriels", "Art / collection", "Autres actifs",
];

const STEPS = [
  { key: "profile", label: "Profil", icon: Building2 },
  { key: "ecosystem", label: "Écosystème", icon: Layers },
  { key: "synthesis", label: "Synthèse", icon: Sparkles },
];

function fmtEur(n) {
  if (n == null) return "—";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)} M€`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)} k€`;
  return `${n} €`;
}

export default function Positionnement() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  // P0 — Modal "Méthodes d'approche" IA pour un acteur précis
  const [approachOpen, setApproachOpen] = useState(null); // {actor, category, geo_level}
  // V23.15.84 — Verrouillage de l'ancre : une fois l'écosystème généré (phase 2),
  // les champs de la phase 1 se grisent. Tous les modules s'appuient dessus.
  const [anchorEdit, setAnchorEdit] = useState(false);
  // P2 — Propagation fractale de la zone d'opération dans la matrice
  const [showDistantCols, setShowDistantCols] = useState(false);

  const [profile, setProfile] = useState({
    sector: "", sector_free: "", geo: "national_federal", size: "", stage: "", description: "",
    zone_operation: { scale: "national_federal", pays: "", region: "", notes: "" },
    zones_cibles: [],
  });
  const [assets, setAssets] = useState([{ category: ASSET_CATEGORIES[0], valeur_eur: 0 }]);

  const load = () =>
    api.get("/workspace/positionnement").then((r) => {
      setData(r.data);
      if (r.data?.profile) {
        const p = r.data.profile;
        setProfile({
          sector: p.sector || "",
          sector_free: p.sector_free || "",
          geo: p.geo || "national_federal",
          size: p.size || "",
          stage: p.stage || "",
          description: p.description || "",
          zone_operation: p.zone_operation || { scale: p.geo || "national_federal", pays: "", region: "", notes: "" },
          zones_cibles: Array.isArray(p.zones_cibles) ? p.zones_cibles : [],
        });
      }
      if (r.data?.collateral?.assets?.length) {
        setAssets(r.data.collateral.assets.map((a) => ({
          category: a.category, valeur_eur: a.valeur_eur,
        })));
      }
    }).catch(() => {});

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // ---------- Step 1: profile ----------
  const saveProfile = async () => {
    if (!profile.sector || !profile.geo) { setErr("Renseignez secteur et zone."); return; }
    setBusy(true); setErr("");
    try {
      await api.put("/workspace/positionnement/profile", profile);
      await load();
      setStep(1);
    } catch (e) { setErr(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  // ---------- Step 2: ecosystem ----------
  const generateEcosystem = async () => {
    setBusy(true); setErr("");
    try {
      await api.post("/workspace/positionnement/ecosystem");
      await load();
    } catch (e) { setErr(e?.response?.data?.detail || "Erreur IA"); }
    finally { setBusy(false); }
  };

  // ---------- Step 3: collateral ----------
  const updateAsset = (i, k, v) => setAssets((arr) => arr.map((a, idx) => idx === i ? { ...a, [k]: v } : a));
  const addAsset = () => setAssets((arr) => [...arr, { category: ASSET_CATEGORIES[0], valeur_eur: 0 }]);
  const removeAsset = (i) => setAssets((arr) => arr.filter((_, idx) => idx !== i));
  const saveCollateral = async () => {
    setBusy(true); setErr("");
    try {
      await api.put("/workspace/positionnement/collateral", {
        assets: assets.map((a) => ({ category: a.category, valeur_eur: parseInt(a.valeur_eur, 10) || 0 })),
      });
      await load();
      setStep(3);
    } catch (e) { setErr(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  // ---------- Step 4: synthesis ----------
  const generateSynthesis = async () => {
    setBusy(true); setErr("");
    try {
      await api.post("/workspace/positionnement/synthesis");
      await load();
    } catch (e) { setErr(e?.response?.data?.detail || "Erreur IA"); }
    finally { setBusy(false); }
  };

  const exportPdf = async () => {
    if (!data?.synthesis) return;
    setBusy(true);
    try {
      const r = await api.post(
        "/workspace/positionnement/pdf",
        {},
        { responseType: "blob" },
      );
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "noam-positionnement.pdf";
      a.click(); window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error("PDF export failed, falling back to print window", e);
      // Fallback: open a printable window with synthesis text — use safe DOM
      // APIs (createElement + textContent) instead of document.write to avoid
      // any chance of HTML injection from the synthesis content.
      const w = window.open("", "_blank");
      if (w) {
        const pre = w.document.createElement("pre");
        pre.style.fontFamily = "Inter, sans-serif";
        pre.style.padding = "32px";
        pre.style.whiteSpace = "pre-wrap";
        pre.textContent = String(data?.synthesis || "");
        w.document.body.appendChild(pre);
        w.print();
      }
    } finally { setBusy(false); }
  };

  const convertToOpportunity = async () => {
    const eco = data?.ecosystem;
    if (!eco) return;
    // Pick the highest-ticket actor across the matrix
    let best = null;
    Object.entries(eco.matrix || {}).forEach(([cat, geos]) => {
      Object.entries(geos || {}).forEach(([g, list]) => {
        (list || []).forEach((a) => {
          if (!best || (a.deal_meur || 0) > (best.deal_meur || 0)) best = { ...a, cat, geo: g };
        });
      });
    });
    if (!best) return;
    setBusy(true);
    try {
      await api.post("/workspace/origination/opportunities", {
        titre: best.acteur,
        secteur: profile.sector || "Autre",
        ticket_mEUR: best.deal_meur || 0,
        etape: "qualification",
        owner: "",
        prob_closing: 30,
        instrument: best.partenariat || "",
      });
      navigate("/app/origination");
    } catch (e) { setErr(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="text-zinc-400">Chargement…</div>;

  // V23.15.84 — L'ancre est verrouillée dès que l'écosystème (phase 2) existe
  const anchorLocked = !!data.ecosystem && !anchorEdit;

  // Step lock: cannot skip ahead without prerequisite
  const canStep = (i) => {
    if (i === 0) return true;
    if (i === 1) return !!data.profile;
    if (i === 2) return !!data.profile;
    return false;
  };

  // V23.14.6e — Une étape est "effectuée" lorsque ses données existent en
  // BD, indépendamment de la position courante du stepper. Sans cela, la
  // 3e étape (Synthèse) n'avait jamais sa coche verte une fois remplie,
  // et le ✓ disparaissait dès qu'on revenait en arrière pour corriger.
  const isStepCompleted = (i) => {
    if (i === 0) return !!data.profile;
    if (i === 1) return !!data.ecosystem;
    if (i === 2) return !!data.synthesis;
    return false;
  };

  return (
    <div className="space-y-6" data-testid="positionnement-root">
      <ThreeQPageHeader endpoint="/workspace/positionnement/top-priority" />
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-[#7F77DD] noam-overline">
            <Compass className="w-3 h-3" /> Tactique · Positionnement stratégique
          </div>
          <h1 className="noam-display text-3xl font-bold tracking-tight mt-1">Module Positionnement</h1>
          <p className="text-zinc-400 text-sm mt-2 max-w-3xl">
            3 étapes pour cartographier votre écosystème et obtenir une lecture stratégique IA. (Collatéral : module Financement.)
          </p>
        </div>
        <ContextButton moduleKey="positionnement" />
      </div>

      {/* Stepper — V23.14.6e : coches persistantes (i.e. la 3e étape coche
          dès que data.synthesis existe) + lignes connectrices pour matérialiser
          la continuité Phase 1 → Phase 2 → Phase 3. */}
      <div className="noam-card p-2 flex items-center gap-0 overflow-x-auto" data-testid="positionnement-stepper">
        {STEPS.map((s, i) => {
          const Icon = s.icon;
          const active = i === step;
          const done = isStepCompleted(i);
          const locked = !canStep(i);
          const nextDone = i < STEPS.length - 1 ? isStepCompleted(i) && isStepCompleted(i + 1) : false;
          return (
            <div key={s.key} className="flex items-center flex-1 min-w-0">
              <button
                onClick={() => !locked && setStep(i)}
                disabled={locked}
                className={`flex-1 min-w-0 sm:min-w-[120px] flex items-center gap-2 px-2 sm:px-3 py-2.5 rounded-md transition-all ${
                  active ? "bg-[#378ADD]/15 border border-[#378ADD]/40" :
                  done ? "text-[#1D9E75]" : "text-zinc-500 hover:text-zinc-300"
                } ${locked ? "opacity-30 cursor-not-allowed" : ""}`}
                data-testid={`positionnement-step-${s.key}`}
              >
                <div className="w-6 h-6 rounded-full flex items-center justify-center shrink-0 text-[11px] font-semibold border"
                  style={active ? { background: "#378ADD33", borderColor: "#378ADD", color: "#fff" }
                    : done ? { background: "#1D9E7533", borderColor: "#1D9E75", color: "#1D9E75" }
                    : { borderColor: "rgba(255,255,255,0.1)", color: "#71717a" }}>
                  {done ? <Check className="w-3.5 h-3.5" /> : <Icon className="w-3.5 h-3.5" />}
                </div>
                <div className="text-left flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">Étape {i + 1}</div>
                  {/* V22.4 — Mobile : label sur 2 lignes max au lieu d'ellipsis qui cache l'info */}
                  <div className={`text-xs font-medium leading-tight ${active ? "text-white" : ""}`}>{s.label}</div>
                </div>
              </button>
              {i < STEPS.length - 1 && (
                <div
                  className="h-0.5 w-6 sm:w-10 shrink-0 transition-colors"
                  style={{ background: nextDone ? "#1D9E75" : "rgba(255,255,255,0.08)" }}
                  data-testid={`positionnement-connector-${i}`}
                  aria-hidden="true"
                />
              )}
            </div>
          );
        })}
      </div>

      {err && (
        <div className="text-sm text-[#E24B4A] bg-[#E24B4A]/10 border border-[#E24B4A]/30 rounded-md px-3 py-2">{err}</div>
      )}

      {/* STEP 1 */}
      {step === 0 && (
        <div className="noam-card p-6 space-y-5" data-testid="step-profile">
          {anchorLocked && (
            <div className="flex items-center justify-between gap-3 p-3 rounded-md border border-[#7F77DD]/30 bg-[#7F77DD]/[0.06]" data-testid="anchor-locked-banner">
              <div className="flex items-center gap-2 text-xs text-zinc-300">
                <Lock className="w-3.5 h-3.5 text-[#7F77DD] flex-shrink-0" />
                <span>
                  <strong className="text-white">Profil ancré.</strong> L'ensemble des modules (Asymétries, Dispersion,
                  News, Radar…) s'appuie sur ces informations. Modifier l'ancre peut décaler leurs lectures.
                </span>
              </div>
              <button
                type="button"
                onClick={() => setAnchorEdit(true)}
                className="btn-ghost text-xs flex-shrink-0"
                data-testid="anchor-unlock-btn"
              >
                <Pencil className="w-3 h-3" /> Modifier l'ancre
              </button>
            </div>
          )}
          <fieldset disabled={anchorLocked} className={anchorLocked ? "space-y-5 opacity-50 pointer-events-none select-none" : "space-y-5"}>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-zinc-400 mb-1 block">Secteur</label>
              <SectorCombobox
                value={profile.sector}
                onChange={(v) => setProfile({ ...profile, sector: v })}
                placeholder="—"
                testId="profile-sector"
              />
            </div>
            <div>
              <label className="text-xs text-zinc-400 mb-1 block">Préciser (libre)</label>
              <input value={profile.sector_free} onChange={(e) => setProfile({ ...profile, sector_free: e.target.value })}
                placeholder="Ex : Banque privée, Fintech, MedTech…"
                className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white"
                data-testid="profile-sector-free" />
            </div>
            <div>
              <label className="text-xs text-zinc-400 mb-1 block flex items-center gap-1.5"><MapPin className="w-3 h-3" /> Zone géographique principale</label>
              <select value={profile.geo} onChange={(e) => setProfile({ ...profile, geo: e.target.value })}
                className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white" data-testid="profile-geo">
                {GEO_OPTIONS.map((g) => <option key={g.v} value={g.v}>{g.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-zinc-400 mb-1 block">Taille</label>
              <select value={profile.size} onChange={(e) => setProfile({ ...profile, size: e.target.value })}
                className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white" data-testid="profile-size">
                <option value="">—</option>
                {SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-zinc-400 mb-1 block">Stade de développement</label>
              <select value={profile.stage} onChange={(e) => setProfile({ ...profile, stage: e.target.value })}
                className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white" data-testid="profile-stage">
                <option value="">—</option>
                {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">Description de l'activité (2-3 phrases)</label>
            <textarea rows={3} value={profile.description}
              onChange={(e) => setProfile({ ...profile, description: e.target.value })}
              placeholder="Décrivez votre activité, vos clients-cibles et votre proposition de valeur."
              className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white resize-none focus:border-[#378ADD] focus:outline-none"
              data-testid="profile-description" />
          </div>

          {/* P0 — Zone d'opération actuelle + Zones cibles */}
          <div className="border-t border-white/10 pt-5 space-y-4">
            <div className="flex items-center gap-2">
              <Target className="w-4 h-4 text-[#1D9E75]" />
              <div className="noam-overline">Zone d'opération actuelle</div>
              <span className="text-[10px] text-zinc-500 italic">— où opérez-vous aujourd'hui ?</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="zone-operation">
              <select
                value={profile.zone_operation?.scale || "national_federal"}
                onChange={(e) => setProfile({ ...profile, zone_operation: { ...(profile.zone_operation || {}), scale: e.target.value } })}
                className="bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white"
                data-testid="zone-op-scale"
              >
                {GEO_OPTIONS.map((g) => <option key={g.v} value={g.v}>{g.label}</option>)}
              </select>
              <input
                type="text" placeholder="Pays (ex: Suisse)"
                value={profile.zone_operation?.pays || ""}
                onChange={(e) => setProfile({ ...profile, zone_operation: { ...(profile.zone_operation || {}), pays: e.target.value } })}
                className="bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white"
                data-testid="zone-op-pays"
              />
              <input
                type="text" placeholder="Région (ex: Genève, Suisse romande)"
                value={profile.zone_operation?.region || ""}
                onChange={(e) => setProfile({ ...profile, zone_operation: { ...(profile.zone_operation || {}), region: e.target.value } })}
                className="bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white"
                data-testid="zone-op-region"
              />
            </div>

            <div className="flex items-center gap-2 mt-4">
              <ArrowRight className="w-4 h-4 text-[#EF9F27]" />
              <div className="noam-overline">Zones d'expansion cibles (1-3 ans)</div>
              <span className="text-[10px] text-zinc-500 italic">— où voulez-vous être demain ? Plusieurs possibles.</span>
              <button
                type="button"
                onClick={() => setProfile({ ...profile, zones_cibles: [...(profile.zones_cibles || []), { scale: "national_federal", pays: "", region: "", notes: "" }] })}
                className="btn-ghost text-xs ml-auto"
                data-testid="zone-cible-add"
              >
                <Plus className="w-3 h-3" /> Ajouter
              </button>
            </div>
            <div className="space-y-2" data-testid="zones-cibles-list">
              {(profile.zones_cibles || []).length === 0 && (
                <p className="text-[11px] text-zinc-500 italic">Aucune zone cible définie. Ajoutez-en pour activer la stratégie d'expansion.</p>
              )}
              {(profile.zones_cibles || []).map((z, i) => (
                <div key={i} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_auto] gap-2 items-center" data-testid={`zone-cible-${i}`}>
                  <select
                    value={z.scale || "national_federal"}
                    onChange={(e) => {
                      const next = [...(profile.zones_cibles || [])];
                      next[i] = { ...next[i], scale: e.target.value };
                      setProfile({ ...profile, zones_cibles: next });
                    }}
                    className="bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white"
                  >
                    {GEO_OPTIONS.map((g) => <option key={g.v} value={g.v}>{g.label}</option>)}
                  </select>
                  <input
                    type="text" placeholder="Pays"
                    value={z.pays || ""}
                    onChange={(e) => {
                      const next = [...(profile.zones_cibles || [])];
                      next[i] = { ...next[i], pays: e.target.value };
                      setProfile({ ...profile, zones_cibles: next });
                    }}
                    className="bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white"
                  />
                  <input
                    type="text" placeholder="Région / Marché"
                    value={z.region || ""}
                    onChange={(e) => {
                      const next = [...(profile.zones_cibles || [])];
                      next[i] = { ...next[i], region: e.target.value };
                      setProfile({ ...profile, zones_cibles: next });
                    }}
                    className="bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white"
                  />
                  <button
                    type="button"
                    onClick={() => setProfile({ ...profile, zones_cibles: (profile.zones_cibles || []).filter((_, j) => j !== i) })}
                    className="text-zinc-500 hover:text-red-400 p-2"
                    data-testid={`zone-cible-${i}-remove`}
                    title="Supprimer"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          </fieldset>

          <div className="flex justify-end">
            {anchorLocked ? (
              <button onClick={() => setStep(1)} className="btn-primary" data-testid="profile-continue-locked">
                <ArrowRight className="w-4 h-4" /> Continuer
              </button>
            ) : (
              <button onClick={saveProfile} disabled={busy} className="btn-primary disabled:opacity-50" data-testid="profile-save-next">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />} {data.ecosystem ? "Valider et re-verrouiller" : "Suivant"}
              </button>
            )}
          </div>
        </div>
      )}

      {/* STEP 2 — ecosystem */}
      {step === 1 && (
        <div className="noam-card p-6 space-y-5" data-testid="step-ecosystem">
          {!data.ecosystem && (
            <div className="text-center py-8">
              <Layers className="w-10 h-10 text-[#378ADD] mx-auto opacity-60" />
              <div className="noam-display text-lg mt-4">Cartographier votre écosystème</div>
              <p className="text-zinc-400 text-sm mt-1 max-w-md mx-auto">
                L'IA analyse votre profil pour identifier les acteurs pertinents à chaque niveau géographique.
              </p>
              <button onClick={generateEcosystem} disabled={busy} className="btn-primary mt-6 mx-auto inline-flex disabled:opacity-50" data-testid="ecosystem-generate">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Générer la matrice
              </button>
            </div>
          )}
          {data.ecosystem && (() => {
            const orderedLevels = data?.ecosystem?.geo_levels_ordered || data?.geo_levels_ordered || GEO_OPTIONS.map((g) => g.v);
            // V23.15.77 — Fix granularité : la matrice affiche les colonnes dans
            // l'ORDRE GÉOGRAPHIQUE CANONIQUE (local → mondial), et non plus dans
            // l'ordre de propagation par distance (qui rendait la carte illisible).
            //
            //   - vue élargie   : les 7 niveaux dans l'ordre local → mondial.
            //   - vue réduite   : fenêtre de 3 niveaux centrée sur la zone d'ancrage,
            //                     toujours dans l'ordre local → mondial.
            //
            // La priorité "distance-à-l'ancrage" reste utilisée pour DÉTERMINER
            // quels niveaux garder en vue réduite, mais l'AFFICHAGE respecte
            // toujours la géographie naturelle.
            const canonicalOrder = GEO_OPTIONS.map((g) => g.v);
            const sortByCanonical = (levels) =>
              [...levels].sort((a, b) => canonicalOrder.indexOf(a) - canonicalOrder.indexOf(b));
            const anchorScale = data?.effective_zone_operation?.scale;
            const anchorIdx = anchorScale ? canonicalOrder.indexOf(anchorScale) : -1;
            let visibleLevels;
            let distantLevels;
            if (showDistantCols) {
              // Vue élargie : tous les niveaux disponibles, ordre canonique
              visibleLevels = sortByCanonical(orderedLevels);
              distantLevels = [];
            } else if (anchorIdx >= 0) {
              // Vue réduite : fenêtre de 3 centrée sur l'ancrage
              let start = Math.max(0, anchorIdx - 1);
              let end = Math.min(canonicalOrder.length, start + 3);
              // Recentre si on tape en bas de la liste
              if (end - start < 3) start = Math.max(0, end - 3);
              const windowLevels = canonicalOrder.slice(start, end).filter((lv) => orderedLevels.includes(lv));
              visibleLevels = sortByCanonical(windowLevels);
              distantLevels = orderedLevels.filter((lv) => !windowLevels.includes(lv));
            } else {
              // Fallback : 3 premiers niveaux dispos, ordre canonique
              const first3 = sortByCanonical(orderedLevels).slice(0, 3);
              visibleLevels = first3;
              distantLevels = orderedLevels.filter((lv) => !first3.includes(lv));
            }
            const geoLabelByValue = Object.fromEntries(GEO_OPTIONS.map((g) => [g.v, g.label]));
            return (
            <>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <div className="noam-overline">Matrice écosystème</div>
                  <div className="noam-display text-base font-semibold">Acteurs identifiés par zone — depuis {data?.effective_zone_operation?.region || data?.effective_zone_operation?.pays || (data?.effective_zone_operation?.scale ? GEO_OPTIONS.find(g => g.v === data.effective_zone_operation.scale)?.label : "votre zone d'opération")}</div>
                </div>
                <button onClick={generateEcosystem} disabled={busy} className="text-xs px-3 py-1.5 rounded-md border border-white/10 hover:bg-white/5 disabled:opacity-50" data-testid="ecosystem-regenerate">
                  {busy ? <Loader2 className="w-3 h-3 animate-spin inline mr-1" /> : <Sparkles className="w-3 h-3 inline mr-1" />} Régénérer
                </button>
              </div>
              <div className="overflow-x-auto -mx-2 px-2">
                <table className="w-full text-sm" data-testid="positionnement-ecosystem-matrix">
                  <thead>
                    <tr className="text-zinc-400 border-b border-white/10">
                      <th className="text-left p-3 sticky left-0 bg-[var(--noam-card-bg)] font-semibold text-[11px] uppercase tracking-wider">Catégorie</th>
                      {visibleLevels.map((lv) => (
                        <th key={lv} className="text-left p-3 font-semibold text-[11px] uppercase tracking-wider min-w-[220px]">
                          {geoLabelByValue[lv] || lv}
                          {lv === data?.effective_zone_operation?.scale && (
                            <span className="ml-1.5 text-[#1D9E75] text-[10px]" title="Votre zone d'ancrage">●</span>
                          )}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(data.ecosystem.categories || []).map((cat) => (
                      <tr key={cat} className="border-t border-white/5 align-top">
                        <td className="p-3 text-white font-semibold text-[13px] sticky left-0 bg-[var(--noam-card-bg)] min-w-[200px] max-w-[220px]">{cat}</td>
                        {visibleLevels.map((lv) => {
                          // P0 fix défensif — NOAM renvoie parfois un objet seul au lieu d'un array
                          // d'objets pour une cellule (cas observé sur Fournisseurs technologiques → regional).
                          // Sans cette normalisation, list.map crashe la page entière.
                          const raw = data.ecosystem.matrix?.[cat]?.[lv];
                          const list = Array.isArray(raw)
                            ? raw
                            : (raw && typeof raw === "object" && raw.acteur ? [raw] : []);
                          const isAnchor = lv === data?.effective_zone_operation?.scale;
                          return (
                            <td key={lv} className={`p-3 min-w-[220px] align-top ${isAnchor ? "bg-[#1D9E75]/[0.05]" : ""}`}>
                              {list.length === 0 && <span className="text-zinc-700 text-[11px]">—</span>}
                              {list.map((a, i) => (
                                <ActorCard
                                  key={`${cat}-${lv}-${i}`}
                                  actor={a}
                                  category={cat}
                                  geoLevel={lv}
                                  onApproach={() => setApproachOpen({ actor: a, category: cat, geo_level: lv })}
                                />
                              ))}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {distantLevels.length > 0 && (
                <div className="flex items-center justify-center">
                  <button
                    type="button"
                    onClick={() => setShowDistantCols(true)}
                    className="text-[10px] text-[#EF9F27] hover:underline inline-flex items-center gap-1"
                    data-testid="positionnement-show-distant"
                  >
                    + Afficher les zones distantes ({distantLevels.map((l) => geoLabelByValue[l]).join(", ")})
                  </button>
                </div>
              )}
              {showDistantCols && (
                <div className="flex items-center justify-center">
                  <button
                    type="button"
                    onClick={() => setShowDistantCols(false)}
                    className="text-[10px] text-zinc-500 hover:text-zinc-300 hover:underline"
                    data-testid="positionnement-hide-distant"
                  >
                    Masquer les zones distantes
                  </button>
                </div>
              )}
              <div className="flex justify-between">
                <button onClick={() => setStep(0)} className="text-sm text-zinc-400 hover:text-white flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> Retour</button>
                <button onClick={() => setStep(2)} className="btn-primary" data-testid="ecosystem-next"><ArrowRight className="w-4 h-4" /> Suivant</button>
              </div>
            </>
            );
          })()}
        </div>
      )}

      {/* STEP 3 — synthesis */}
      {step === 2 && (
        <div className="noam-card p-6 space-y-5" data-testid="step-synthesis">
          {!data.synthesis && (
            <div className="text-center py-8">
              <Sparkles className="w-10 h-10 text-[#378ADD] mx-auto opacity-60" />
              <div className="noam-display text-lg mt-4">Lecture stratégique IA</div>
              <p className="text-zinc-400 text-sm mt-1 max-w-md mx-auto">
                NOAM génère votre positionnement, leviers et partenaires prioritaires.
              </p>
              <button onClick={generateSynthesis} disabled={busy} className="btn-primary mt-6 mx-auto inline-flex disabled:opacity-50" data-testid="synthesis-generate">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Générer la synthèse
              </button>
            </div>
          )}
          {data.synthesis && (
            <>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <div className="noam-overline">Lecture IA · Positionnement stratégique</div>
                  <div className="noam-display text-base font-semibold flex items-center gap-2 flex-wrap">
                    Synthèse Noam
                    <FreshnessBadge
                      lastGeneratedAt={data.updated_at}
                      moduleLabel="Synthèse Positionnement"
                      onRegenerate={busy ? null : generateSynthesis}
                      regenerating={busy}
                      testid="positionnement-synthesis-freshness"
                    />
                  </div>
                </div>
                <button onClick={generateSynthesis} disabled={busy} className="text-xs px-3 py-1.5 rounded-md border border-white/10 hover:bg-white/5 disabled:opacity-50">
                  <Sparkles className="w-3 h-3 inline mr-1" /> Régénérer
                </button>
              </div>
              <div className="text-zinc-200 leading-relaxed border-l-2 border-[#378ADD] pl-4 whitespace-pre-line text-sm" data-testid="synthesis-text">
                {data.synthesis}
              </div>
              <div className="flex flex-wrap items-center gap-3 pt-3 border-t border-white/5">
                <CopyButton text={data.synthesis} label="Copier" />
                <PersonaAction action="export_pdf">
                  <button onClick={exportPdf} disabled={busy} className="text-xs px-3 py-1.5 rounded-md border border-white/10 hover:bg-white/5 disabled:opacity-50 flex items-center gap-1.5" data-testid="synthesis-pdf">
                    <FileText className="w-3 h-3" /> Exporter en PDF
                  </button>
                </PersonaAction>
                {data.ecosystem && (
                  <button onClick={convertToOpportunity} disabled={busy} className="btn-primary text-xs disabled:opacity-50 ml-auto" data-testid="synthesis-create-opp">
                    <Target className="w-3.5 h-3.5" /> Créer une opportunité dans Origination
                  </button>
                )}
              </div>
            </>
          )}
          <div className="flex justify-between">
            <button onClick={() => setStep(1)} className="text-sm text-zinc-400 hover:text-white flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> Retour</button>
          </div>
        </div>
      )}

      {approachOpen && (
        <ApproachModal
          payload={approachOpen}
          onClose={() => setApproachOpen(null)}
        />
      )}
    </div>
  );
}

// P0 — Carte d'acteur Positionnement avec actions Mapper + Méthodes d'approche
function ActorCard({ actor, category, geoLevel, onApproach }) {
  const [mapping, setMapping] = useState(false);
  const [mappedTo, setMappedTo] = useState(null);
  const [leadState, setLeadState] = useState(null); // null | "loading" | "done"

  const toLead = async () => {
    setLeadState("loading");
    try {
      const r = await api.post("/workspace/positionnement/actor/to-lead", {
        acteur: actor.acteur,
        partenariat: actor.partenariat,
        category,
        geo_level: geoLevel,
        deal_meur: actor.deal_meur,
      });
      setLeadState("done");
      notify.success(r.data?.already_exists ? `${actor.acteur} déjà dans vos Leads.` : `${actor.acteur} converti en Lead ✓`);
    } catch (e) { notify.error(fmtError(e, "Conversion en Lead impossible.")); setLeadState(null); }
  };

  const mapIt = async () => {
    setMapping(true);
    try {
      const r = await api.post("/workspace/positionnement/actor/to-utility-map", {
        acteur: actor.acteur,
        partenariat: actor.partenariat,
        category,
        geo_level: geoLevel,
        deal_meur: actor.deal_meur,
      });
      setMappedTo(r.data);
      if (r.data?.already_exists) {
        notify.success(`${actor.acteur} déjà présent dans « ${r.data.map_name || "Utility Mapping"} ».`);
      } else {
        notify.success(`${actor.acteur} ajouté à « ${r.data.map_name} ».`);
      }
    } catch (e) { notify.error(fmtError(e, "Mapping impossible.")); }
    finally { setMapping(false); }
  };

  return (
    <div className="border border-white/10 rounded-md p-2 mb-1.5 bg-white/[0.03] hover:border-white/25 transition-colors" data-testid={`positionnement-actor-${category}-${geoLevel}`}>
      <div className="text-[13px] text-white font-medium leading-tight">{actor.acteur}</div>
      {actor.partenariat && (
        <div className="text-[11px] text-zinc-400 mt-1 leading-snug">{actor.partenariat}</div>
      )}
      {actor.deal_meur != null && (
        <div className="text-[11px] text-[#1D9E75] font-semibold mt-1">~ {actor.deal_meur} M€</div>
      )}
      <div className="flex items-center gap-1.5 mt-2 pt-2 border-t border-white/10 flex-wrap">
        <SplcButton entityName={actor.acteur} testid={`positionnement-actor-splc-${category}-${geoLevel}`} />
        <button
          type="button"
          onClick={mapIt}
          disabled={mapping}
          className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded border inline-flex items-center gap-1 transition-colors ${
            mappedTo ? "border-[#1D9E75]/50 text-[#1D9E75] bg-[#1D9E75]/5" : "border-white/15 text-zinc-300 hover:text-white hover:border-white/40"
          }`}
          title="Ajouter cet acteur à Utility Mapping"
          data-testid={`positionnement-actor-map-${category}-${geoLevel}`}
        >
          {mapping ? <Loader2 className="w-3 h-3 animate-spin" /> : (mappedTo ? <Check className="w-3 h-3" /> : <Network className="w-3 h-3" />)}
          {mappedTo ? "Mappé" : "Mapper"}
        </button>
        <button
          type="button"
          onClick={onApproach}
          className="text-[10px] uppercase tracking-wider px-2 py-1 rounded border border-white/15 text-zinc-300 hover:text-[#EF9F27] hover:border-[#EF9F27]/50 inline-flex items-center gap-1 transition-colors"
          title="Générer un plan d'approche IA"
          data-testid={`positionnement-actor-approach-${category}-${geoLevel}`}
        >
          <MessageCircle className="w-3 h-3" />
          Approche
        </button>
        <button
          type="button"
          onClick={toLead}
          disabled={leadState === "loading" || leadState === "done"}
          className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded border inline-flex items-center gap-1 transition-colors disabled:opacity-60 ${
            leadState === "done" ? "border-emerald-500/50 text-emerald-400 bg-emerald-500/5" : "border-white/15 text-zinc-300 hover:text-[#378ADD] hover:border-[#378ADD]/50"
          }`}
          title="Convertir cet acteur en Lead (origination)"
          data-testid={`positionnement-actor-lead-${category}-${geoLevel}`}
        >
          {leadState === "loading" ? <Loader2 className="w-3 h-3 animate-spin" /> : leadState === "done" ? <Check className="w-3 h-3" /> : <UserPlus className="w-3 h-3" />}
          {leadState === "done" ? "Lead" : "→ Lead"}
        </button>
      </div>
    </div>
  );
}

// P0 — Modal Méthodes d'approche IA pour un acteur Positionnement
function ApproachModal({ payload, onClose }) {
  const [busy, setBusy] = useState(true);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    const run = async () => {
      try {
        const r = await api.post("/workspace/positionnement/actor/approach-methods", {
          acteur: payload.actor.acteur,
          partenariat: payload.actor.partenariat,
          category: payload.category,
          geo_level: payload.geo_level,
          deal_meur: payload.actor.deal_meur,
        });
        setData(r.data);
      } catch (e) { setErr(fmtError(e, "Génération IA impossible.")); }
      finally { setBusy(false); }
    };
    run();
  }, [payload]);

  const m = data?.methods || {};

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="noam-card w-full max-w-3xl my-8" onClick={(e) => e.stopPropagation()} data-testid="positionnement-approach-modal">
        <div className="flex items-start justify-between p-5 border-b border-white/5">
          <div>
            <div className="noam-overline flex items-center gap-1.5">
              <MessageCircle className="w-3 h-3 text-[#EF9F27]" /> Plan d'approche IA
            </div>
            <div className="noam-display text-base font-semibold mt-0.5" data-testid="positionnement-approach-actor">
              {payload.actor.acteur}
            </div>
            <div className="text-[11px] text-zinc-400 mt-0.5">
              {payload.category} · {payload.geo_level} {payload.actor.partenariat && `· ${payload.actor.partenariat}`}
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-white/5"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-5 space-y-4 max-h-[75vh] overflow-y-auto">
          {busy && (
            <div className="text-center py-12 text-zinc-500">
              <Loader2 className="w-6 h-6 animate-spin inline mb-2" />
              <p className="text-xs">NOAM génère un plan d'approche concret (15-30s)…</p>
            </div>
          )}
          {err && (
            <div className="p-3 rounded border border-[#E24B4A]/30 bg-[#E24B4A]/10 text-[#FCA5A5] text-xs">{err}</div>
          )}
          {!busy && !err && data && (
            <>
              {m.angle_attaque && (
                <Section icon={Target} color="#EF9F27" title="Angle d'attaque">
                  <p className="text-sm text-zinc-200 leading-relaxed">{m.angle_attaque}</p>
                </Section>
              )}

              {m.intro_chaude?.length > 0 && (
                <Section icon={Network} color="#1D9E75" title="Intro chaude (via intermédiaires)">
                  <ul className="space-y-1.5">
                    {m.intro_chaude.map((x, i) => <li key={i} className="text-xs text-zinc-300 flex items-start gap-2"><span className="text-[#1D9E75]">›</span> {x}</li>)}
                  </ul>
                </Section>
              )}

              {m.intro_froide?.length > 0 && (
                <Section icon={MessageCircle} color="#378ADD" title="Intro froide (canaux directs)">
                  <ul className="space-y-1.5">
                    {m.intro_froide.map((x, i) => <li key={i} className="text-xs text-zinc-300 flex items-start gap-2"><span className="text-[#378ADD]">›</span> {x}</li>)}
                  </ul>
                </Section>
              )}

              {m.intermediaires?.length > 0 && (
                <Section icon={Building2} color="#C26EE7" title="Intermédiaires à activer">
                  <ul className="space-y-1.5">
                    {m.intermediaires.map((x, i) => <li key={i} className="text-xs text-zinc-300 flex items-start gap-2"><span className="text-[#C26EE7]">›</span> {x}</li>)}
                  </ul>
                </Section>
              )}

              {m.premier_contact && (
                <Section icon={Sparkles} color="#EF9F27" title="Premier contact suggéré">
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Canal : <span className="text-zinc-200">{m.premier_contact.canal}</span></div>
                  <div className="p-3 rounded bg-[#EF9F27]/5 border border-[#EF9F27]/20 text-sm text-zinc-200 italic">"{m.premier_contact.accroche}"</div>
                </Section>
              )}

              {m.risques?.length > 0 && (
                <Section icon={Trash2} color="#E24B4A" title="Pièges à éviter">
                  <ul className="space-y-1.5">
                    {m.risques.map((x, i) => <li key={i} className="text-xs text-zinc-300 flex items-start gap-2"><span className="text-[#E24B4A]">⚠</span> {x}</li>)}
                  </ul>
                </Section>
              )}

              {m.timing && (
                <Section icon={Compass} color="#A78BFA" title="Timing">
                  <p className="text-xs text-zinc-300">{m.timing}</p>
                </Section>
              )}

              {data.web_sources?.length > 0 && (
                <div className="pt-3 border-t border-white/5">
                  <div className="text-[9px] uppercase tracking-wider text-zinc-600 mb-1">Sources web Tavily</div>
                  <ul className="space-y-0.5">
                    {data.web_sources.map((s, i) => (
                      <li key={i} className="text-[10px] text-zinc-500 truncate">
                        <a href={s.url} target="_blank" rel="noopener noreferrer" className="hover:text-[#378ADD]">{s.title}</a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ icon: Icon, color, title, children }) {
  return (
    <div className="noam-card p-3">
      <div className="flex items-center gap-1.5 mb-2 noam-overline" style={{ color }}>
        <Icon className="w-3 h-3" /> {title}
      </div>
      {children}
    </div>
  );
}
